import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, Slack, FileCode, FolderKanban, GitBranch, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();
  const [step, setStep] = useState(1);

  const tools = [
    { name: 'Slack', icon: Slack, color: 'bg-[#4A154B]' },
    { name: 'Jira', icon: FileCode, color: 'bg-[#0052CC]' },
    { name: 'GitHub', icon: GitBranch, color: 'bg-[#181717]' },
  ];

  const handleComplete = () => {
    completeOnboarding();
    toast.success('Setup complete! Welcome to Focus Pane');
    navigate('/feed');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="onboarding-page">
      <div className="w-full max-w-2xl">
        {step === 1 && (
          <div className="text-center space-y-6">
            <h1 className="text-4xl font-bold">Bring your work into one feed</h1>
            <p className="text-xl text-muted-foreground">
              Focus Pane watches your tools and surfaces what matters, so you don't have to dig through tabs.
            </p>
            <div className="flex gap-4 justify-center">
              <Button size="lg" onClick={() => setStep(2)} data-testid="get-started-btn">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" onClick={handleComplete}>
                Skip for now
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">Connect your tools</h2>
              <p className="text-muted-foreground">
                Pick the tools you rely on every day. You can add or remove them any time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <Card key={tool.name} className="p-6 cursor-pointer hover:border-primary" data-testid={`tool-${tool.name.toLowerCase()}`}>
                    <div className="flex flex-col items-center space-y-4">
                      <div className={`w-16 h-16 rounded-lg ${tool.color} flex items-center justify-center`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-center">
                        <h3 className="font-semibold">{tool.name}</h3>
                        <p className="text-sm text-muted-foreground">Connect</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="flex gap-4">
              <Button onClick={() => setStep(3)} data-testid="continue-btn">
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="ghost" onClick={() => setStep(1)}>
                Back
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">You're all set!</h2>
              <p className="text-muted-foreground">
                Your workspace is ready. Start creating projects and connecting your tools to see updates flow in.
              </p>
            </div>

            <div className="bg-muted p-6 rounded-lg space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary-foreground" />
                </div>
                <span>Workspace created</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary-foreground" />
                </div>
                <span>Ready to connect tools</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary-foreground" />
                </div>
                <span>Projects can be created</span>
              </div>
            </div>

            <Button onClick={handleComplete} size="lg" data-testid="finish-onboarding-btn">
              Go to Feed
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
